import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getBillingBanner, createPortalSession } from '../../api/admin';
import type { BillingBanner } from '../../shared/types';
import { daysUntil, longDate, money } from '../platform/planDisplay';
import { ApiRequestError } from '../../api/client';
import BannerStrip from './BannerStrip';

/**
 * The free-period and dunning banners (frames 1e and 1i), one component at
 * the top of every admin page.
 *
 * State is derived fresh from the banner endpoint on every route change —
 * never from a flag set at grant time — so an extension, a revocation or a
 * paid invoice shows up on the next page load without anyone clearing
 * anything. None of these are dismissible while the state is live: a
 * surprise charge is the one failure mode worth being slightly annoying
 * to avoid.
 */
export default function TrialBanner({ canBill }: { canBill: boolean }) {
  const [banner, setBanner] = useState<BillingBanner | null>(null);
  const location = useLocation();

  useEffect(() => {
    let live = true;
    getBillingBanner()
      .then((b) => { if (live) setBanner(b); })
      // A shell chrome element must never take the page down with it — an
      // older server without the endpoint, or a 403, just means no banner.
      .catch(() => { if (live) setBanner(null); });
    return () => { live = false; };
  }, [location.pathname]);

  if (!banner) return null;
  const { trial, recentTrial, dunning } = banner;
  if (!trial && !recentTrial && !dunning) return null;

  return (
    <BannerStrip>
      {trial && <LiveTrialBanner trial={trial} />}
      {!trial && recentTrial && <RecentTrialBanner recent={recentTrial} />}
      {dunning && <DunningBanner dunning={dunning} canBill={canBill} />}
    </BannerStrip>
  );
}

/**
 * Sends the tab to the Stripe-hosted portal; billing pages own the errors.
 *
 * Same tab on purpose: the portal's own "return to Form-Magic" link comes
 * back to this app, so a new tab would leave the customer holding two of them.
 */
function usePortal(): [boolean, () => void] {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const { url } = await createPortalSession();
      window.location.assign(url);
      return; // navigating away — stay disabled rather than flicker back
    } catch (e) {
      if (!(e instanceof ApiRequestError)) throw e;
      // Stripe unconnected — the Billing page states this honestly; the
      // banner button simply cannot help and says nothing false.
    } finally {
      setBusy(false);
    }
  };
  return [busy, open];
}

/** " at $29/month", or nothing for a plan with no price (Free). */
const priceOf = (cents: number | null | undefined, per: string): string =>
  (cents ? ` at ${money(cents)}/${per}` : '');

/**
 * D3: a period that does not convert names where it goes back to — " You go
 * back to Team at $29/month." — when the grant recorded it; older rows say
 * nothing more than they always did.
 */
const backTo = (trial: NonNullable<BillingBanner['trial']>, per: string): string =>
  (trial.endsSubscription ? ' The subscription ends with it and the account moves to Free.'
    : trial.previousPlanName ? ` You go back to ${trial.previousPlanName}${priceOf(trial.previousAmountCents, per)}.` : '');

function LiveTrialBanner({ trial }: { trial: NonNullable<BillingBanner['trial']> }) {
  const left = daysUntil(trial.expiresAt);
  const per = trial.interval === 'year' ? 'year' : 'month';
  const endDate = longDate(trial.expiresAt);

  if (left > 7) {
    return (
      <div className="bl-banner bl-trial">
        <span className="bl-banner-pill">Free trial</span>
        <span className="bl-banner-text">
          <b>You&apos;re on {trial.planName} free until {endDate}.</b>{' '}
          {trial.autoConvert && trial.convertAmountCents !== null
            ? <>On {endDate} we&apos;ll charge the card on file {money(trial.convertAmountCents)}/{per} unless you cancel before then.</>
            : <>On {endDate} it ends and nothing is charged.{backTo(trial, per)}</>}
        </span>
        <Link className="bl-banner-link" to="/admin/settings/billing">Manage plan</Link>
      </div>
    );
  }

  return (
    <div className="bl-banner bl-soon">
      <span className="bl-banner-pill">
        {left} day{left === 1 ? '' : 's'} left
      </span>
      <span className="bl-banner-text">
        <b>Your free {trial.planName} period ends on {endDate}.</b>{' '}
        {trial.autoConvert && trial.convertAmountCents !== null
          ? <>We&apos;ll charge your card <b>{money(trial.convertAmountCents)}</b> that day and keep you on {trial.planName}. Nothing to do if that&apos;s what you want.</>
          : trial.endsSubscription
            ? <>The subscription ends that day, the account moves to Free, and nothing is charged.</>
            : trial.previousPlanName
              ? <>You&apos;ll go back to {trial.previousPlanName}{priceOf(trial.previousAmountCents, per)} that day and nothing is charged.</>
              : <>You&apos;ll go back to your paid plan that day and nothing is charged.</>}
      </span>
      <Link className="bl-banner-link" to="/admin/settings/billing/plans">Change or cancel</Link>
    </div>
  );
}

function RecentTrialBanner({ recent }: { recent: NonNullable<BillingBanner['recentTrial']> }) {
  if (recent.status === 'converted') {
    return (
      <div className="bl-banner bl-ok">
        <span className="bl-banner-pill">Now paid</span>
        <span className="bl-banner-text">
          <b>You&apos;re on {recent.planName}.</b>{' '}
          Your card was charged on {longDate(recent.resolvedAt)}; the receipt is in Billing.
        </span>
        <Link className="bl-banner-link" to="/admin/settings/billing">View receipt ↗</Link>
      </div>
    );
  }

  // Ended without converting, or revoked by staff — the same grey statement.
  const ro = recent.readOnlyForms || [];
  return (
    <div className="bl-banner bl-ended">
      <span className="bl-banner-pill">
        Back on {recent.paidPlanName}
      </span>
      <span className="bl-banner-text">
        <b>Your free {recent.planName} period ended on {longDate(recent.resolvedAt)} and you weren&apos;t charged.</b>{' '}
        You&apos;re back on {recent.paidPlanName}.
        {ro.length > 0 && (
          <>
            {' '}<b>{ro.length} form{ro.length === 1 ? ' is' : 's are'}</b> now above the {recent.paidPlanName} limits
            and read-only until you upgrade or remove some — {ro.map((f) => f.name).join(', ')}. Nothing was deleted.
          </>
        )}
      </span>
      <Link className="bl-banner-link" to="/admin/settings/billing/plans">See what&apos;s affected</Link>
    </div>
  );
}

/** Small counts read like a person wrote them: "after three attempts". */
const SMALL = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const countWord = (n: number) => SMALL[n] || String(n);

function DunningBanner({ dunning, canBill }: {
  dunning: NonNullable<BillingBanner['dunning']>; canBill: boolean;
}) {
  const [portalBusy, openPortal] = usePortal();

  if (dunning.status === 'moved_to_free') {
    const movedOn = dunning.finalAttemptAt || dunning.graceUntil || dunning.firstFailedAt;
    const ro = dunning.readOnlyForms || [];
    return (
      <div className="bl-banner bl-ended">
        <span className="bl-banner-pill">On Free</span>
        <span className="bl-banner-text">
          <b>This account moved to Free{movedOn ? ` on ${longDate(movedOn)}` : ''} because {money(dunning.amountCents)} went unpaid.</b>{' '}
          All {dunning.keptForms.toLocaleString()} forms and {dunning.keptSubmissions.toLocaleString()} submissions are
          still here.{ro.length > 0 && <> {ro.length} form{ro.length === 1 ? ' is' : 's are'} read-only.</>}{' '}
          {/* The subscription was cancelled at the move (Ben, 2026-09-27), so paying the
              old invoice brings nothing back: choosing the plan again does. */}
          Choose {dunning.restorePlanName} again to get it back.
        </span>
        <span className="bl-banner-actions">
          {canBill && (
            <Link className="fs-btn sm primary" to="/admin/settings/billing/plans">
              Choose {dunning.restorePlanName} again
            </Link>
          )}
          <Link className="fs-btn sm neutral" to="/admin">Export everything</Link>
        </span>
      </div>
    );
  }

  // past_due — amber at the first failure, red once the retries have run out
  // of road: three attempts spent, or within 7 days of the move to Free — the
  // end of the 15-day grace (Ben, 2026-09-27), which the webhook starts.
  const movesOn = dunning.graceUntil || dunning.finalAttemptAt;
  const nearFinal = movesOn !== null && daysUntil(movesOn) <= 7;
  const late = dunning.attemptCount >= 3 || nearFinal;

  if (!late) {
    return (
      <div className="bl-banner bl-amber">
        <span className="bl-banner-pill">Payment failed</span>
        <span className="bl-banner-text">
          <b>We couldn&apos;t charge your card for {money(dunning.amountCents)}.</b>{' '}
          We&apos;ll try again automatically — nothing changes{movesOn ? <> before {longDate(movesOn)}</> : ' in the meantime'}.
        </span>
        {canBill && (
          <button className="fs-btn sm primary" disabled={portalBusy} onClick={openPortal}>Update card</button>
        )}
      </div>
    );
  }

  const ro = dunning.readOnlyForms || [];
  const overdue = dunning.firstFailedAt
    ? Math.max(1, Math.round((Date.now() - new Date(dunning.firstFailedAt).getTime()) / 86400000))
    : null;
  return (
    <div className="bl-banner bl-red">
      <span className="bl-banner-pill">
        {overdue !== null ? `${overdue} day${overdue === 1 ? '' : 's'} overdue` : 'Overdue'}
      </span>
      <span className="bl-banner-text">
        <b>{money(dunning.amountCents)} is still unpaid after {countWord(dunning.attemptCount)} attempt{dunning.attemptCount === 1 ? '' : 's'}.</b>{' '}
        {movesOn && <>On {longDate(movesOn)} this account moves to Free: your forms keep collecting
        {ro.length > 0 && <> and {ro.length} form{ro.length === 1 ? ' goes' : 's go'} read-only</>}. </>}
        Nothing is deleted, ever.
      </span>
      {canBill && (
        <button className="fs-btn sm bl-btn-red" disabled={portalBusy} onClick={openPortal}>Pay now</button>
      )}
    </div>
  );
}
